/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */
import MsDyn365, { ICoreContext } from '@msdyn365-commerce/core';
import { IHeaderViewProps } from '@msdyn365-commerce-modules/header';
import { addThrottledEvent, Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import set from 'lodash/set';

/**
 * GridSizes.
 */
export type GridSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

const headerView: React.FC<IHeaderViewProps> = props => {
    const {
        HeaderTag,
        HeaderContainer,
        HeaderTopBarContainer,
        MobileMenuContainer,
        MobileMenuBodyContainer,
        MobileMenuLinksContainer,
        app: {
            config: { enableZendesk, zendeskKey }
        }
    } = props;

    //@ts-ignore
    const [viewport, setViewport] = React.useState<GridSize>('lg');
    const [isMobile, setIsMobile] = React.useState<boolean>(false);

    const updateViewport = () => {
        const viewport = getViewport(props.context);
        setViewport(viewport);
        setIsMobile(viewport === 'xs' || viewport === 'sm');
    };

    React.useEffect(() => {
        const script = document.createElement('script');
        script.src = `https://static.zdassets.com/ekr/snippet.js?key=${zendeskKey}`;
        script.async = true;
        script.id = 'ze-snippet';
        const loadZendesk = () => {
            document.body.appendChild(script);
            script.onload = () => {
                const liveChatButton = document.querySelector('[aria-label="Live Chat"][title="Live Chat"]');
                if (liveChatButton) {
                    liveChatButton.addEventListener('click', event => {
                        event.preventDefault(); // Prevent default link behavior

                        // Check if Zendesk script is loaded
                        //@ts-ignore
                        if (typeof zE !== 'undefined') {
                            //@ts-ignore
                            zE('messenger', 'open');
                        } else {
                            console.error('Zendesk Web Widget is not initialized');
                        }
                    });
                } else {
                    console.error('Live Chat button not found');
                }
            };

            script.onerror = () => {
                console.error('Failed to load Zendesk script');
            };
        };

        const closeWebWidget = () => {
            //@ts-ignore
            if (typeof zE !== 'undefined') {
                //@ts-ignore
                zE('messenger', 'close');
            }
        };

        if (MsDyn365.isBrowser && enableZendesk && zendeskKey && !document.getElementById('ze-snippet')) {
            loadZendesk();
            window.addEventListener('beforeunload', closeWebWidget);
        }

        const scriptCleanup = () => {
            document.body.removeChild(script);
            window.removeEventListener('beforeunload', closeWebWidget);
        };
        return () => {
            scriptCleanup();
        };
    }, []);

    React.useEffect(() => {
        updateViewport();
        if (MsDyn365.isBrowser) {
            addThrottledEvent(window, 'resize', updateViewport as EventListener);
        }
        // Cleanup
        return () => {
            window.removeEventListener('resize', updateViewport);
        };
    }, []);

    return (
        <Module {...HeaderTag}>
            <Node {...HeaderContainer}>
                <Node {...HeaderTopBarContainer}>
                    <div className='meriton-header__container'>
                        <div className='meriton-header__row'>
                            {!isMobile && props.navIcon}

                            {isMobile && (
                                <div className='hamburger'>
                                    <div className='mobile-navicon-container'>{props.navIcon}</div>
                                </div>
                            )}
                            <div className='meriton-logo'>
                                <div className='ms-header__logo'>{props.logo}</div>
                            </div>
                            {!isMobile && (
                                <div className='meriton-search__desktop'>
                                    <div className='ms-search with-submit'>{_renderReactFragment(props.search)}</div>
                                </div>
                            )}
                            {props.preferredStore}
                            {props.currentDistributor}
                            <div className='meriton-mobile__btn'>
                                <div className='ms-header__account-info account-desktop'>
                                    {' '}
                                    {_renderDesktopAccountBlock(props, isMobile)}
                                </div>
                            </div>
                            {_renderReactFragment(props.siteOptions)}
                            <div className='meriton-cart'>
                                <div className='ms-cart-icon'>{props.cartIcon}</div>
                            </div>
                            {isMobile && (
                                <div className='meriton-search'>
                                    <div className='ms-search with-submit'>{_renderReactFragment(props.search)}</div>
                                </div>
                            )}
                        </div>
                    </div>
                </Node>
                <Node {...MobileMenuContainer}>
                    <Node {...MobileMenuBodyContainer}>
                        {props.MobileMenuHeader}
                        {_renderReactFragment(props.menuBar)}
                        <Node {...MobileMenuLinksContainer}>
                            {props.accountLinks ? props.accountLinks.map(link => link) : false}
                            {props.siteOptions}
                            {props.wishListIconMobile}
                            {props.signInLink}
                            {props.signOutLink}
                        </Node>
                    </Node>
                </Node>
                {_renderReactFragment(props.menuBar)}
            </Node>
        </Module>
    );
};

function _renderDesktopAccountBlock(props: IHeaderViewProps, isMobile: boolean): JSX.Element | null {
    const {
        AccountInfoDropdownParentContainer,
        AccountInfoDropdownPopoverConentContainer,
        accountInfoDropdownButton,
        signOutLink,
        signInLink,
        accountLinks
    } = props;

    if (props.context.request.user.isAuthenticated) {
        //We are updating users Name based on Customer Name available in context
        const customerName = !isMobile ? props.context.request.user.name! : extractInitials(props.context.request.user.name!);
        set(accountInfoDropdownButton as Object, 'props.children.props.children[0].props.children', customerName);
    }
    if (AccountInfoDropdownParentContainer) {
        if (AccountInfoDropdownPopoverConentContainer) {
            return (
                <Node {...AccountInfoDropdownParentContainer}>
                    {accountInfoDropdownButton}
                    <Node {...AccountInfoDropdownPopoverConentContainer}>
                        {accountLinks ? accountLinks.map(link => link) : false}
                        {signOutLink}
                    </Node>
                </Node>
            );
        } else if (signInLink) {
            return <Node {...AccountInfoDropdownParentContainer}>{signInLink}</Node>;
        }
    }
    props.context.telemetry.error('Header content is empty, module wont render.');
    return null;
}

function _renderReactFragment(items: React.ReactNode[]): JSX.Element | null {
    return (
        <>
            {items && items.length > 0
                ? items.map((slot: React.ReactNode, index: number) => {
                      return <React.Fragment key={index}>{slot}</React.Fragment>;
                  })
                : null}
        </>
    );
}

function getViewport(
    context: ICoreContext<{
        [x: string]: any;
    }>
): GridSize {
    if (context.request.device && context.request.device.Type === 'Mobile') {
        return 'xs'; // Always render as mobile if the device is mobile
    }

    if (window.innerWidth) {
        const gridSettings = context.request.gridSettings;
        if (gridSettings) {
            if (gridSettings.xs && window.innerWidth <= gridSettings.xs.w) {
                return 'xs';
            } else if (gridSettings.sm && window.innerWidth <= gridSettings.sm.w) {
                return 'sm';
            } else if (gridSettings.md && window.innerWidth < gridSettings.md.w) {
                return 'md';
            } else if (gridSettings.lg && window.innerWidth <= gridSettings.lg.w) {
                return 'lg';
            }
            return 'xl'; // Default to 'xl' if no conditions match
        }
    }

    return 'xs'; // Default to 'xs' if no grid settings or window width are available
}

function extractInitials(name: string): string {
    const initials = name
        .split(' ')
        .map(word => word.charAt(0).toUpperCase())
        .join('');
    return initials;
}

export default headerView;
